/**
 * Ciclo de vida ÚNICO de actualización de la aplicación.
 *
 * Problema que corrige: existían dos caminos distintos (usePWA + updateChecker)
 * y el aviso "Nueva versión disponible" podía quedarse pegado o repetirse:
 *  - `update()` sólo funcionaba si ya existía un trabajador en espera
 *    (`waiting`); si aún estaba instalando, el clic no hacía nada.
 *  - se recargaba de inmediato, sin esperar a que el trabajador nuevo tomara
 *    el control, así que la recarga podía volver a servir la versión vieja.
 *
 * Aquí vive la lógica pura (posponer 24 h, guardia anti-bucle de recarga) para
 * poder probarla, y el flujo real de activación.
 */

import { APP_VERSION } from '@/lib/appVersion';

export const UPDATE_DEFER_KEY = 'plaxter-update-deferred';
export const UPDATE_RELOAD_KEY = 'plaxter-update-reloading';
export const DEFER_WINDOW_MS = 24 * 60 * 60 * 1000;

export interface SimpleStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface DeferRecord {
  until: number;
  version: string;
}

function safeParse(raw: string | null): DeferRecord | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<DeferRecord>;
    if (typeof parsed?.until !== 'number' || typeof parsed?.version !== 'string') return null;
    return { until: parsed.until, version: parsed.version };
  } catch {
    return null;
  }
}

/** Pospone el aviso 24 h, atado a la versión que corre ahora mismo. */
export function deferUpdate(
  storage: SimpleStorage,
  now: number = Date.now(),
  version: string = APP_VERSION,
): void {
  try {
    storage.setItem(UPDATE_DEFER_KEY, JSON.stringify({ until: now + DEFER_WINDOW_MS, version }));
  } catch {
    // almacenamiento no disponible: simplemente no se pospone
  }
}

/**
 * true mientras el usuario haya pospuesto ESTA versión y no hayan pasado 24 h.
 * Una versión futura vuelve a avisar aunque el usuario haya pospuesto la anterior.
 */
export function isUpdateDeferred(
  storage: SimpleStorage,
  now: number = Date.now(),
  version: string = APP_VERSION,
): boolean {
  let record: DeferRecord | null = null;
  try {
    record = safeParse(storage.getItem(UPDATE_DEFER_KEY));
  } catch {
    return false;
  }
  if (!record) return false;
  if (record.version !== version) return false;
  return record.until > now;
}

export function clearUpdateDeferral(storage: SimpleStorage): void {
  try {
    storage.removeItem(UPDATE_DEFER_KEY);
  } catch {
    // ignorar
  }
}

/**
 * Guardia anti-bucle: devuelve true la primera vez y false en las siguientes,
 * de modo que una misma activación no puede provocar recargas repetidas.
 */
export function claimReloadSlot(
  storage: SimpleStorage,
  version: string = APP_VERSION,
): boolean {
  try {
    if (storage.getItem(UPDATE_RELOAD_KEY) === version) return false;
    storage.setItem(UPDATE_RELOAD_KEY, version);
    return true;
  } catch {
    return true;
  }
}

export function releaseReloadSlot(storage: SimpleStorage): void {
  try {
    storage.removeItem(UPDATE_RELOAD_KEY);
  } catch {
    // ignorar
  }
}

function browserSession(): SimpleStorage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function browserLocal(): SimpleStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** API de conveniencia para la interfaz (localStorage real). */
export const updateDeferral = {
  defer: () => {
    const s = browserLocal();
    if (s) deferUpdate(s);
  },
  isDeferred: () => {
    const s = browserLocal();
    return s ? isUpdateDeferred(s) : false;
  },
  clear: () => {
    const s = browserLocal();
    if (s) clearUpdateDeferral(s);
  },
};

function reloadOnce(): void {
  const session = browserSession();
  if (session && !claimReloadSlot(session)) return;
  window.location.reload();
}

/**
 * Espera a que el trabajador nuevo tome el control y recarga UNA sola vez.
 * Soporta los tres estados: instalando, en espera y ya activado.
 * Nunca borra cachés ajenas ni datos del usuario.
 */
export async function activateUpdate(timeoutMs = 10000): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    reloadOnce();
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      reloadOnce();
      return;
    }

    const target = registration.waiting ?? registration.installing;
    if (!target) {
      // Nada pendiente: forzar una comprobación y recargar con red fresca.
      await registration.update().catch(() => undefined);
      reloadOnce();
      return;
    }

    await new Promise<void>(resolve => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        navigator.serviceWorker.removeEventListener('controllerchange', finish);
        resolve();
      };

      const timer = window.setTimeout(finish, timeoutMs);
      navigator.serviceWorker.addEventListener('controllerchange', finish);

      const skip = () => target.postMessage({ type: 'SKIP_WAITING' });

      if (target.state === 'installed') {
        skip();
      } else if (target.state === 'activated') {
        finish();
      } else {
        target.addEventListener('statechange', () => {
          if (target.state === 'installed') skip();
          if (target.state === 'activated') finish();
          if (target.state === 'redundant') finish();
        });
      }
    });
  } catch {
    // seguir adelante: la recarga sigue siendo la salida correcta
  }

  updateDeferral.clear();
  reloadOnce();
}

/** Limpia la guardia tras un arranque normal (se llama al cargar la app). */
export function resetReloadGuard(): void {
  const session = browserSession();
  if (session) releaseReloadSlot(session);
}
